import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import TechChart from "./parts/TechChart"
import RelativeChart from "./parts/RelativeChart"
import ChartLegFilter from "./parts/ChartLegFilter"
import ChartPlotFiler from "./parts/ChartPlotFilter"
import ChartToolButtons from "./parts/ChartToolButtons"
import ChartAddon from "./parts/ChartAddon"
import SubChart from "./parts/SubChart"
import ChartDatePicker from "./parts/ChartDatePicker"
import ChartLegCount from "./parts/ChartLegCount"
import ChartPriceDetail from "./parts/ChartPriceDetail"
import ToolsPropety from "./parts/ChartToolsPropety"
import useChartInitialize from "./parts/useChartInitialize"
import ChartFigureButtons from "./parts/ChartFigureButtons"
import useDruggableDialog from "./logic/useDruggableDialog"
import _, { isArray } from "lodash"
import ClassNames from "classnames"
import { SearchDetail } from "components/utils/search"
import { APIResponse } from "redux/apis/types"
import { changeSymbol } from "redux/ducks/Chart"
import RelativeSymbolSelector from "./parts/RelativeSymbolSelector"
import ChartPropetyResetModal from "./parts/ChartPropetyResetModal"
import ChartInfoDialog from "./parts/ChartInfoDialog"
import { setSubSymbol } from "./logic/chartSymbolAndLeg"
import { UserStorage } from "utils/userStorage"
import { PLOTTYPE } from "./logic/chartConfig"
import {
  setThemeFunction,
  CheckHasProperties,
  checkParentWindow,
} from "utils/util"
import { chartColorSetting } from "redux/ducks/Chart"
import chartColors from "components/atoms/chart/logic/chartColor"
import Toast from "components/pages/others/toast"
import { CoverLoading } from "components/atoms/utils"
import { Disclaimer } from "components/organisms/panel/disclaimer"
import SessionTimeOut from "components/pages/main/sessionTimeOut"

interface props {
  canvasId?: string
  setViewType?: Function
  setVisible?: Function
  setResizeFlag?: Function
  resizeFlag?: boolean
}

const comparisonTarget = ["Exchange", "SecurityType", "Symbol"]

export const ChartFlagContext = React.createContext<{
  // scroll操作時のフラグ。scrollの表示期間は、F#側に期間に任せているため、scroll操作時はtrueにすることで、js側の期間変更処理をスキップする。
  scrollFlag?: {
    flag: boolean
    setFlag: Function
  }
}>({})

const Chart = React.memo(() => {
  const cheackUserStorage = (data: object) => {
    const targetProperies = [
      "addonChartOptions",
      "cycleAnaTypes",
      "plotType",
      "priceVolumeParam",
      "subChartOptions",
      "symbol",
      "subSymbol",
      "tenkanKikan",
      "term",
      "addonChart",
      "addonSelected",
      "subChart",
      "subSelected",
      "tools",
    ]

    if (!CheckHasProperties(data, targetProperies)) return false
    const extraData = _.filter(Object.keys(data), key => {
      return !_.includes(targetProperies, key)
    })
    if (!_.isEmpty(...extraData)) return false
    _.forEach(data, (items, key) => {
      if (
        _.includes(
          [
            "addonChart",
            "addonChartOptions",
            "addonSelected",
            "cycleAnaTypes",
            "subChart",
            "subChartOptions",
            "subSelected",
            "subSymbol",
            "tools",
          ],
          key
        )
      ) {
        if (!isArray(items)) return false
      }
      if (_.includes(["plotType", "priceVolumeParam", "symbol", "term"], key)) {
        if (typeof items !== "object") return false
      }
      if (_.includes(["tenkanKikan"], key)) {
        if (typeof items !== "number") return false
      }
    })
    return true
  }
  const techChartProperties = useMemo(() => {
    const storageData = UserStorage.TechChart.get()
    if (!cheackUserStorage(storageData)) UserStorage.TechChart.remove()
    const data = cheackUserStorage(storageData) ? storageData : null
    return data
  }, [])
  const canvasId = useChartInitialize({
    chartType: "tech",
    data: techChartProperties || null,
  })
  // ラインボタンは3行目に出るため、チャートのキャンバスのリサイズのFlagとして使用
  const [resizeFlag, setResizeFlag] = useState(false)
  const dispatch = useDispatch()
  const providersSelector = (state: any) => state.chart[canvasId].providers
  const plotTypeSelector = (state: any) => state.chart[canvasId].plotType
  const colorSettingSelector = (state: any) => state.chart.colorSetting

  const providers = useSelector(providersSelector)
  const plotType = useSelector(plotTypeSelector)
  const colorSetting = useSelector(colorSettingSelector)
  const [_provider, _controller, _procs] = useMemo(() => {
    return providers
  }, [providers])

  const [scrollFlag, setScrollFlag] = useState(false)

  useEffect(() => {
    checkParentWindow()
  }, [])

  useEffect(() => {
    setThemeFunction()
    const elements = document.querySelector("html").className.split(" ")
    const ChartColors = _.includes(elements, "dark")
      ? UserStorage.TechChartColor.getDark()
      : UserStorage.TechChartColor.getWhite()
    dispatch(
      !_.isEmpty(ChartColors)
        ? chartColorSetting(ChartColors)
        : chartColorSetting(chartColors.initialize())
    )
  }, [])

  useEffect(() => {
    if (!_controller) return
    const elements = document.querySelector("html").className.split(" ")
    if (_.includes(elements, "dark")) {
      UserStorage.TechChartColor.setDark(colorSetting)
    } else {
      UserStorage.TechChartColor.setWhite(colorSetting)
    }
    chartColors.setColorPattern(plotType.key, colorSetting, _controller)
  }, [colorSetting])

  return (
    <ChartFlagContext.Provider
      value={{ scrollFlag: { flag: scrollFlag, setFlag: setScrollFlag } }}
    >
      <div className="tech-chart-wrapper">
        <div className="tech-chart-header">
          <ChartHeader
            canvasId={canvasId}
            setResizeFlag={setResizeFlag}
            resizeFlag={resizeFlag}
          />
        </div>
        <div
          className={ClassNames("tech-chart-content", {
            "line-setting-display": resizeFlag,
          })}
        >
          <TechChart canvasId={canvasId} resizeFlag={resizeFlag} />
        </div>
      </div>
      <Disclaimer />
      <CoverLoading targetCls="tech-chart-wrapper" />
      <SessionTimeOut />
    </ChartFlagContext.Provider>
  )
})

const ChartHeader = React.memo((props: props) => {
  const { canvasId, resizeFlag, setResizeFlag } = props
  const [techVisible, setTechVisible] = useState(false)
  const [lineVisible, setlineVisible] = useState(false)
  const [infoDialogVisible, setInfoDialogVisible] = useState(false)
  const [themeColorFlag, changeThemeColorFlag] = useState(false)
  const [focusEffect, setFocusEffect] = useState(false)
  const plotTypeSelector = (state: any) => state.chart[canvasId].plotType
  const symbolSelector = (state: any) => state.chart[canvasId].symbol
  const plotType = useSelector(plotTypeSelector)
  const symbol = useSelector(symbolSelector)
  const PandFFlag = useMemo(() => {
    return plotType.id === PLOTTYPE[3].id
  }, [plotType])

  const dispatch = useDispatch()
  const style = useDruggableDialog({
    id: "tech-setting",
    position: { top: "9.6rem", left: "1.6rem" },
    priority: 1,
  })
  const infoDialogStyle = useDruggableDialog({
    id: "chart-info-modal",
    position: { top: "9.6rem", left: "1.6rem" },
  })
  const _setTechVisible = () => {
    setTechVisible(!techVisible)
  }
  const _setLineVisible = () => {
    setlineVisible(!lineVisible)
    setResizeFlag(!resizeFlag)
  }

  useEffect(() => {
    setFocusEffect(true)
  }, [])

  useEffect(() => {
    if (plotType.id !== PLOTTYPE[3].id) return
    setlineVisible(false)
    setResizeFlag(false)
  }, [plotType])

  return (
    <>
      <div className="chart-filter-col first-row">
        <div className="price-detail-group">
          <SearchDetail
            className="search-bar active"
            placeholder="銘柄コード/銘柄名"
            executeFunction={(data: APIResponse.Res) => {
              if (
                _.isEqual(
                  _.pick(data, comparisonTarget),
                  _.pick(symbol, comparisonTarget)
                )
              )
                return
              dispatch(changeSymbol(canvasId, data))
            }}
            hasCountryBtn={true}
            isDirect={true}
            chartType={1}
            noMemory={true}
            inputId="search-chart"
            focusEffect={focusEffect}
            setFocusEffect={setFocusEffect}
          />
          <ChartPriceDetail canvasId={canvasId} oneLineType={true} />
        </div>
        <div className="first-row-contents">
          <div className="first-row-items">
            <RelativeSymbolSelector canvasId={canvasId} />
          </div>
          <div className="first-row-items">
            <RelativeChart canvasId={canvasId} />
          </div>
        </div>
        <div className="first-row-contents">
          <div className="btn-multi-control">
            <label className="btn btn-outline shaped theme-color-btn">
              <input
                type="checkbox"
                className="input-multi-control"
                onChange={() => {
                  changeThemeColorFlag(true)
                  const elements = document.querySelector("html").classList
                  if (_.includes(elements, "dark")) {
                    elements.add("bright")
                    elements.remove("dark")
                  } else {
                    elements.add("dark")
                    elements.remove("bright")
                  }
                  const ChartColors = _.includes(elements, "dark")
                    ? UserStorage.TechChartColor.getDark()
                    : UserStorage.TechChartColor.getWhite()
                  dispatch(
                    !_.isEmpty(ChartColors)
                      ? chartColorSetting(ChartColors)
                      : chartColorSetting(chartColors.initialize())
                  )
                }}
                checked={false}
              />
              <i className="material-icons">color_lens</i>
              <div>テーマ切替</div>
            </label>
          </div>
        </div>
      </div>
      <div className="chart-filter-col second-row">
        <div className="chart-filter-col-inner">
          <div className="filter-control-item">
            <ChartPlotFiler canvasId={canvasId} />
          </div>
          <div className="filter-control-item">
            <ChartLegFilter canvasId={canvasId} />
          </div>
          <ChartLegCount canvasId={canvasId} />
          <ChartDatePicker canvasId={canvasId} />
          <ChartToolButtons
            canvasId={canvasId}
            infoDialogVisible={infoDialogVisible}
            setInfoDaialogVisible={setInfoDialogVisible}
            themeColorFlag={themeColorFlag}
            changeThemeColorFlag={changeThemeColorFlag}
          />
        </div>
        <div className="btn-multi-control setting-modal-button">
          <label className="btn btn-outline shaped">
            <input
              type="checkbox"
              className="input-multi-control"
              onChange={() => _setTechVisible()}
              checked={techVisible}
            />
            <span className="ripple">テクニカル</span>
          </label>
        </div>
        <div
          className={ClassNames("btn-multi-control setting-modal-button", {
            active: plotType.id !== PLOTTYPE[3].id,
          })}
        >
          <label className="btn btn-outline shaped">
            <input
              type="checkbox"
              className="input-multi-control"
              onChange={() => {
                if (plotType.id === PLOTTYPE[3].id) return
                _setLineVisible()
              }}
              checked={lineVisible}
            />
            <span className="ripple">ライン</span>
          </label>
        </div>
        <div id="tech-setting" style={style}>
          {techVisible && (
            <ShowAddonPropety
              canvasId={canvasId}
              setVisible={_setTechVisible}
            />
          )}
        </div>
        <div id="chart-info-modal" style={infoDialogStyle}>
          {infoDialogVisible && !PandFFlag && (
            <ChartInfoDialog
              canvasId={canvasId}
              setVisible={setInfoDialogVisible}
            />
          )}
        </div>
      </div>
      {lineVisible && (
        <div className="chart-filter-col third-row">
          <ChartFigureButtons canvasId={canvasId} />
        </div>
      )}
    </>
  )
})

export const useChartLabels = (listType = false, dataNone = false) => {
  const [viewType, setViewType] = useState(null)
  const [viewList, setViewList] = useState([])
  const _setViewType = (id: number) => {
    if (viewType === id) return setViewType(null)
    setViewType(id)
  }
  const _setViewList = (id: number) => {
    const _viewList = _.clone(viewList)
    if (_.includes(_viewList, id)) {
      _.pull(_viewList, id)
      if (!dataNone && _.isEmpty(_viewList)) return
    } else {
      _viewList.push(id)
    }
    setViewList(_viewList)
  }
  if (listType) return [viewList, _setViewList]
  return [viewType, _setViewType]
}

const ADDON_PROPETY_LABELS = [
  { displayTitle: "アドオン", id: 0 },
  { displayTitle: "サブ", id: 1 },
]

const ShowAddonPropety = React.memo((props: props) => {
  const { canvasId } = props
  const [viewType, setViewType] = useChartLabels(true)
  const [visibleModal, setVisibleModal] = useState(false)
  useEffect(() => {
    setViewType(0)
  }, [])

  return (
    <div className="chart-setting-modal">
      <Toast />
      <div className="chart-setting-buttons">
        {_.map(ADDON_PROPETY_LABELS, items => {
          return (
            <div key={items.id} className="btn-multi-control">
              <label className="btn btn-outline shaped">
                <input
                  type="checkbox"
                  className="input-multi-control"
                  onChange={() => setViewType(items.id)}
                  checked={_.includes(viewType, items.id)}
                />
                <span className="ripple">{items.displayTitle}</span>
              </label>
            </div>
          )
        })}
        <div className="btn-multi-control">
          <label className="btn btn-outline shaped">
            <input
              type="checkbox"
              className="input-multi-control"
              onChange={() => setVisibleModal(true)}
              checked={visibleModal}
            />
            <span className="ripple">初期化</span>
          </label>
        </div>
        <i className="material-icons" onClick={() => props.setVisible()}>
          close
        </i>
      </div>
      <div className="chart-setting-content-wrap">
        <div className="chart-setting-content">
          {_.includes(viewType, 0) && (
            <AddonAndToolsPropety canvasId={canvasId} />
          )}
          {_.includes(viewType, 1) && <SubChart canvasId={canvasId} />}
        </div>
      </div>
      <ChartPropetyResetModal
        isVisible={visibleModal}
        setVisible={setVisibleModal}
        canvasId={canvasId}
      />
    </div>
  )
})

const AddonAndToolsPropety = React.memo((props: props) => {
  const { canvasId } = props
  return (
    <div>
      <ChartAddon canvasId={canvasId} />
      <ToolsPropety canvasId={canvasId} />
    </div>
  )
})

export default Chart
